// @ts-strict-ignore
import { FC, ReactNode, SVGProps } from 'react'

import { AcsCatBlockProps } from '@/screens/Accessories/page/types'

import { HeroInfoContentProps } from '@/widgets/HeroInfoBlock/ui/components/HeroInfoContent/HeroInfoContent'
import { HeroInfoTabsProps } from '@/widgets/HeroInfoBlock/ui/components/HeroInfoTabs/HeroInfoTabs'
import { Shop } from '@/widgets/ShopsMapNew/types/shop'

import { TCartItem } from '@/entities/cart'
import { CatalogItem } from '@/entities/catalog/types'

import { TProduct } from '@/shared/types/product-types'
import { AnimationDirection } from '@/shared/ui/Animation/Animation'

export type Nullable<T> = T | null

export type TBurger = {
	isOpen: boolean
	activePage: { sectionId: string; direction: AnimationDirection }
}

export type InfoBlockProps = {
	title: string
	subtitle?: string
	imgSrc?: string
	items?: {
		title?: string
		titleBold?: string
		text?: string
	}[]
	icon?: string
}
export type TServerSection = {
	ID: string
	CODE: string
	PARENT_ID?: string | null
	NAME: string
	PICTURE?: string | null
	UF_SHOW_TOP_MENU?: boolean | null
	UF_SHOW_FOOTER_MENU?: boolean | null
	DESCRIPTION?: string | null
	UF_COMPARE_LINK?: string | null
	UF_COMPARE_PICTURE?: string | null
	UF_ACCESSORIES_LINK?: string | null
	UF_ACCESSORIES_PICTURE?: string | null
	UF_DONT_SHOW_IN_LIST?: boolean | null
	COUNT?: string | null
	SEO?: TSeoResponse
	LINK: string
}

export type TServerBrandSection = {
	SECTION_META_DESCRIPTION: string
	SECTION_META_TITLE: string
	SECTION_PAGE_TITLE: string
}

export type TPage = {
	id?: string
	link?: string
	title: string
	parentId?: string | null
	items?: TSection[]
	targetBlank?: boolean
	Icon?: FC<JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>>
}

export type TSection = TPage & {
	code?: string
	imgSrc?: string | null
	description?: string | null
	isInTopMenu?: boolean | null
	isInFooterMenu?: boolean | null
	UF_COMPARE_LINK?: string | null
	UF_COMPARE_PICTURE?: string | null
	UF_ACCESSORIES_LINK?: string | null
	UF_ACCESSORIES_PICTURE?: string | null
	UF_DONT_SHOW_IN_LIST?: boolean | null
	COUNT?: string | null
	seo?: TSeo
	icon?: string
	hidden?: boolean
	Icon?: FC<JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>>
}

export type TPrice = {
	VALUE: number
	PRICE_TYPE?: string
}

/**
 * @deprecated use TProduct from @nbcom/types
 */
export type _TProduct = TProduct

export type TPromotion = {
	CODE: string
	ID: number
	NAME: string
	PREVIEW_TEXT: string
	SHORT_NAME: string
	SORT: number
	CUSTOM_LINK?: string
}

export type TSeoResponse = {
	ELEMENT_DETAIL_PICTURE_FILE_ALT?: string
	ELEMENT_META_DESCRIPTION?: string
	ELEMENT_META_TITLE?: string
	ELEMENT_PAGE_TITLE?: string
	ELEMENT_PREVIEW_PICTURE_FILE_ALT?: string
	SECTION_DETAIL_PICTURE_FILE_ALT?: string
	SECTION_SEO_DESCRIPTION?: string
	SECTION_META_DESCRIPTION?: string
	SECTION_META_KEYWORDS?: string
	SECTION_META_TITLE?: string
	SECTION_PAGE_TITLE?: string
	SECTION_PICTURE_FILE_ALT?: string
	DESCRIPTION?: string
}

export type TSeo = {
	elementDetailPictureFileAlt?: string
	elementMetaDescription?: string
	elementMetaTitle?: string
	elementPageTitle?: string
	elementPreviewPictureFileAlt?: string
	sectionDetailPictureFileAlt?: string
	sectionPictureFileAlt?: string
	sectionMetaDescription?: string
	sectionMetaKeywords?: string
	sectionMetaTitle?: string
	sectionPageTitle?: string
	description?: string
	canonicalLink?: string
}

/**
 * @deprecated use @nbcom/types
 */
export type TAccessories = Record<string, TAccessoryGroup>

/**
 * @deprecated use @nbcom/types
 */
export type TAccessoryGroup = {
	ACCESSORIES?: Record<string, TAccessory>
	SECTION_ID: number
	SECTION_NAME: string
}

/**
 * @deprecated use @nbcom/types
 */
export type TAccessory = _TProduct & {
	mainProductCode?: TProductCode
	PRICES?: {
		PRICE: TPrice
		OLD_PRICE: TPrice
	}
	quantity?: number
}

export type TRelatedVariants = {
	KOD: string
	TYPE: string
	VALUE: string
	LABEL: string
	IS_CURRENT: boolean
	HASH: string
	IN_STOCK: string
}

export type TError = {
	code: number | null
	message: string | null
	customData: string | null
	status?: boolean
}

export type TServiceID = string
export type TActiveService = {
	id: TServiceID
	quantity: number
	price: number
	title: string
}

export enum TooltipPlacement {
	Bottom = `bottom`,
	BottomLeft = `bottomLeft`,
	BottomRight = `bottomRight`,
	Top = `top`,
	TopLeft = `topLeft`,
	TopRight = `topRight`,
	Left = `left`,
	Right = `right`
}

export type TTooltipPlacement = `${TooltipPlacement}`

export type TCustomPopoverTooltip = {
	tooltipId: string
	popoverIsActive: boolean
	placement?: TTooltipPlacement
	tooltipToggleElement: HTMLElement
	type?: `primary` | `default` | `black`
	children?: ReactNode
}

export type PositionStyles = {
	top?: string
	left?: string
}

export type CustomPopoverProps = {
	nowrap?: boolean
	placement?: TTooltipPlacement
	content: string
	trigger?: string
	title?: string
	type?: `primary` | `default` | `black`
	disabled?: boolean
	children?: ReactNode
}

export type CategoryItemPropsTemporary = {
	title: string
	link: string
	imgSrc?: string
	items?: CategoryItemPropsTemporary[]
	parent?: string
	targetBlank?: boolean
}

/**
 * @deprecated use @nbcom/types
 */
export type TProductCode = string

/**
 * @deprecated use @nbcom/types
 */
export type TServiceVariant = {
	ID: string
	TITLE: string
	DESCRIPTION?: string[]
	ICON_SRC?: string | null
	PRICE?: number
	KOD: string
	SECTION_ID?: number
	DETAIL_PICTURE?: string | null
}

export type TService = {
	ID: string
	TITLE: string
	SHORT_TITLE: string
	DESCRIPTION?: string
	VARIANTS: TServiceVariant[]
	TYPE?: string
}

export type TServiceServer = {
	[serviceKey: string]: {
		TYPE: string
		SECTION_SORT: number
		ITEMS: {
			ID: number
			PRICE: number
			NAME: string
			TEXT: string
			PIC: string | null
			DETAIL_PICTURE: string | null
			KOD: string
			SECTION_ID: number
			DESCRIPTION: string[]
		}[]
	}
}

export type SquareCatItemProps = Omit<CatalogItem, 'PREVIEW_PICTURE' | 'ID'> & {
	PICTURE: string
}

export type TAccessoryPageData = {
	SECTION_META_TITLE: string
	SECTION_META_DESCRIPTION: string
	assignment: SquareCatItemProps[]
	popular: SquareCatItemProps[]
	category: AcsCatBlockProps[]
	sections?: SquareCatItemProps[]
	H1: string
}

export type TFaqItem = {
	answer: string
	question: string
}

export type TShopsResponse = Shop[]

export type TShop = {
	id: string
	name: string
	fullAddress: string
	textDescription: string
	amount?: string
	region: string
	schedule: string
	images: string[]
	gpsN: string
	gpsS: string
	phone: string
	mall: string
	metro: string
	metroLine: string
	isService?: boolean
	isShop?: boolean
	textInfo?: string
	deliveryDate?: Nullable<string>
	xmlId?: string
	schemeImage?: string
}

export type TUserData = {
	first_name: string | null
	middle_name?: string | null
	last_name?: string | null
	user_birthdate?: string | null
	user_gender?: string
	user_phone?: string
	user_email?: string
	user_id?: number | null
	user_name?: string
}

export type TServiceItem = {
	ID: string
	XML_ID: string
	NAME: string
	SORT: string
	LINK_VALUE: string
	PREVIEW_PICTURE: string
	TYPE_VALUE: string
	TYPE_NAME: string
	DESCRIPTION?: string
}

export enum FilterDisplayType {
	RangeWithSlider = `A`,
	Range = `B`,
	CheckBox = `F`,
	CheckBoxWithPicture = `G`,
	CheckBoxWithLabelAndPicture = `H`,
	Radio = `K`,
	Select = `P`,
	SelectWithLabelAndPicture = `R`
}

export enum FilterPropertyType {
	Number = `N`,
	List = `L`
}

export enum FilterJsonType {
	Price = `><`,
	Default = `=`
}

export type AntFormItems = Record<string, true | string | number | undefined | []>

export enum ProductType {
	Service = `service`,
	Accessory = `accessory`,
	Product = `product`,
	TradeIn = `tradein`,
	Total = `total`
}

export type CartInfoTypes = {
	products: TCartItem[]
	basePrice: number
	price: number
}

export type SetCartInfoProps = {
	PRODUCTS: TCartItem[]
	OLD_PRICE: number
	BONUS_ACCRUED: number
	PRICE: number
	SHOW_ORDER_BUTTON?: boolean
	DISCOUNT_LIST: DiscountItem[]
	discounts: CouponProps[]
	saleUidCookie?: string
	BONUS_PAY: number
}

export type DiscountItem = {
	NAME: string
	AMOUNT: number
}

export type TAddProductRequest = {
	ID: number
	KOD?: string
	TITLE?: string
	RELATED_PRODUCTS?: number
	TYPE_PRODUCTS?: `${ProductType}`
	PRICE?: TPrice
	SECTION_ID?: number | string
	SECTION_NAME?: string
	location?: `${CatalogItemLocation}`
	updateBasket?: boolean
	blockUpdate?: boolean
}

export type SetItemCountRequestProps = {
	ID: number
	quantity: number
	updateBasket?: boolean
	type: QuantityChangeType
	location?: CatalogItemLocation
}
export type TRemoveItemRequest = {
	ID: number
	RELATED_ID?: number
	updateBasket?: boolean
	blockUpdate?: boolean
	location?: `${CatalogItemLocation}`
	SECTION_NAME?: string
}
export type CouponProps = {
	couponCode: string
	id: number
	isCoupon: string
	name: string
}

export type LocalProduct = {
	SECTION_ID?: any
} & TCartItem

export type RemoveProductInLocalListProps = {
	location?: `${CatalogItemLocation}`
} & TCartItem

export enum QuantityChangeType {
	Increase = `increase`,
	Decrease = `decrease`
}

export enum ProductCardTypes {
	Main = `main`,
	Accessories = `accessories`,
	Wide = `wide`,
	MiniReadMore = `miniReadMore`,
	MiniBuy = `miniBuy`,
	Mobile = `mobile`,
	Compare = `compare`,
	Discount = `discount`,
	Hit = `hit`,
	MiniCard = `mini`,
	B2bDiscount = `b2bDiscount`
}

export enum CatalogItemLocation {
	Listing = `Catalog Grid`,
	ProductCard = `Product Card`,
	SalesSection = `Sale Timers`,
	NewProductsSection = `New Goods`,
	SalesPage = `Sales`,
	Cart = `Cart`,
	FavoritesPage = `Favorites`,
	Popular = `Popular products`,
	Variants = `Multi Card`,
	ComparePage = `Compare Row`,
	BasketPageMain = `Basket Grid`,
	BasketPageAccessories = `Basket Accessories`,
	ProductPageAccessories = `Product Accessories`,
	SearchPopup = `Search Clue`,
	SearchResults = `Search Results`,
	ServicesCart = `Services cart`,
	PopularProduct = ``,
	AccessoriesCart = `Accessories car`,
	None = `not set`
}

export enum PayTypeNames {
	Online = `ONLINE`,
	OnReceipt = `ON_RECEIPT`,
	Credit = `CREDIT`,
	Instalments = `INSTALLMENT`
}

export type TDeliveryType = {
	ID: string
	CODE: string
	PARENT_ID: string
	NAME: string
	ACTIVE: `Y` | `N`
	DESCRIPTION?: string
	LOGOTIP: string
	CONFIG: DeliveryItemConfigType
	CURRENCY: string
	XML_ID: string
	RESTRICTIONS?: TDeliveryRestrictions
}

export type TDeliveryRestrictions = {
	BY_LOCATION?: string[]
	BY_PAY_SYSTEM?: TPaymentsRestrictions[]
	BY_PRICE?: {
		CURRENCY: string
		MAX_PRICE: string
		MIN_PRICE: string
	}
	BY_TIME?: {
		MIN_HOUR: string
		MAX_HOUR: string
	}
	BY_SITE_ID?: string[]
	BY_PERSON?: string[]
}

export type TPaymentsRestrictions = {
	PS_ID: string
	PS_NAME: string
}

export type DeliveryItemConfigType = {
	MAIN: {
		CURRENCY: string
		PRICE: string
		PERIOD: DeliveryItemPeriodType
	}
}

export type DeliveryItemPeriodType = {
	FROM: number
	TO: number
	TYPE: string
}

export type TGetCartInfoRequest = {
	saleUidCookie?: string
	coupon?: string
	cookie?: string
}

export type RemoveLocalListProps = {
	ID: number
}

export enum RegionSelectorType {
	Burger = `burger`,
	Product = `product`,
	Header = `header`,
	Shops = `shops`
}

export type TPromotionProducts = {
	TOTAL: number
	PER_PAGE: number
	CURRENT_PAGE: number
	LAST_PAGE: number
	ITEMS: _TProduct[]
	IS_LAST_PAGE?: boolean
}

export enum SortType {
	PopularityToLow = `popular`,
	PriceLowToHigh = `price|asc`,
	PriceHighToLow = `price|desc`,
	None = ``
}

export enum DiscountSortType {
	PriceLowToHigh = `price|asc`,
	PriceHighToLow = `price|desc`
}

export type TCatalogDiscountFilterItem = {
	ID: string
	VALUE: string
}

export type TDiscountServerResponse = {
	PRODUCTS: TDiscountProductItem[]
	SECTION_LINK: string
}

export type TMarkdownServerResponse = {
	PRODUCTS: any
	SECTION_LINK: string
}

export type TDiscountProductItem = {
	CONDITION: string
	ENGINEERS_COMMENT: string | null
	ITEM_SECTION: string | null
	ITEM_IMAGE: string | null
	KOD: string
	DISCOUNT_KOD: string
	MARKDOWN_REASON: string | null
	NAME?: string
	PART_NUMBER: string | null
	PRICE: string | null
	OLD_PRICE: string | null
	BUNDLE?: string | null
	SORT: number | null
	STOCK_ID: string | null
	STOCK_NAME: string | null
	WARRANTY_EXPIRE: string | null
	WORKABILITY: string | null
	IS_SERVICE: string | null
	DISCOUNT_TYPE: string | null
	STOCK_CODE?: string | null
	BATTERY_SIZE?: string | null
	IS_NEW_BATTERY?: boolean
	IMAGES: string[]
	TITLE?: string
	PROMOTION_DESCRIPTION?: string
	PROMOTION_TAG?: string
	ID: string
}

export interface TSeoLinkNew {
	NAME: string
	LINK: string
}

export type TAddTradeInProductsAction = {
	productList: TCartItem[]
	updateBasket?: boolean
}

export type TPromotionCategories = {
	CATEGORY_NAME: string
	CATEGORY_ID: string
	CATEGORY_CODE: string
}[]

export interface TBitrixInstance {
	open: () => void
	close: () => void
	setCustomData: ([TBitrixCustomData]) => void
}

export type TBitrixPage = {
	ID: string
	NAME: string
	CODE: string
	DETAIL_TEXT: string
	SEO: TSeoResponse
}

export type THit = { NAME: string; PRODUCTS: _TProduct[] }

export type TRecommendedProducts = { PRODUCTS: _TProduct[] }

export type TPromoSlider = {
	KOD: string
	PRICE?: number
	WITHOUT_PROMOTION?: boolean
	SORT?: string
	PROMOTION_TEXT?: string
	LINK?: string
}

export type TPromoSliderLink = {
	title: string
	link: string
}

export type TPromoHeroInfoBlock = {
	imgSrc?: string
	bgColor?: string
} & HeroInfoContentProps &
	HeroInfoTabsProps

export type TPersonalNavItem = {
	name: string
	link: string
}

export type TBurgerLevel1 = {
	NAME: string
	ID: string
	SECTION_LINK: string
	SECTION_ID: string | null
	ICON?: string
	COLOR?: string
	HIDDEN?: boolean
	SHOW_ALL_TEXT?: boolean
	SHOW_TOP_MENU?: boolean
	SHOW_SUBSECTIONS?: string
	SUB_MENU: TBurgerLevel2[]
	TYPE: string
	PICTURE?: Nullable<string>
}

export type TBurgerLevel2 = {
	NAME: string
	COLOR?: string
	HIDDEN?: boolean
	SHOW_SUBSECTIONS?: string
	LINKS: TBurgerLevel3[]
	COLUMN?: number
	COUNT: number
	PICTURE?: string
	TYPE?: string
	SECTION_LINK: string
}

export type TBurgerLevel3 = {
	NAME: string
	LINK: string
	IMAGE?: string
	HIDDEN?: boolean
}

export type TBurgerOutput = {
	hidden: boolean
	items: TBurgerOutput[]
	icon: string | null
	title: string
	link: string
	id: string
	parentId?: string
	TYPE?: string
}

export enum RegionType {
	Burger = `burger`,
	Product = `product`,
	Header = `header`,
	Shops = `shops`
}

export type CatalogBannerConfig = {
	UF_CATALOG_BANNER_ID?: number
	UF_CATALOG_BANNER_NAME?: string
	UF_CATALOG_BANNER?: string
	UF_CATALOG_BANNER_MOBILE?: string
	UF_CATALOG_BANNER_LINK?: string
	UF_CATALOG_BANNER_MIDDLE_ID?: number
	UF_CATALOG_BANNER_MIDDLE_NAME?: string
	UF_CATALOG_MIDDLE_BANNER?: string
	UF_CATALOG_BANNER_MIDDLE_MOBILE?: string
	UF_CATALOG_BANNER_MIDDLE_LINK?: string
	UF_CATALOG_IS_HIDDEN_BANNER: boolean
}

export interface ServiceCategories {
	ID: string
	CODE: string
	NAME: string
	PARENT_ID?: Nullable<string>
	PICTURE?: string
	XML_ID: string
	DEPTH_LEVEL: string
}
